import React, {useEffect, useState} from 'react';
import Layout from "../../components/Layout/Layout";
import {
    ArrowUpOutlined,
    FileOutlined,
    FileTextOutlined,
    FolderFilled,
    RightOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import {Breadcrumb, Empty, Input, Spin, message} from "antd";
import {MainService} from "../../services/MainService";
import {FileModel} from "../../models/FileModel";
import {FileModelType} from "../../config/Constants";
import EditFileModal, {SelectedFileSchema} from "./components/EditFileModal";
import {ApiErrorData} from "../../models/ApiResponse";
import {ItemInterface} from "../../models/ItemInterface";

import './FileEditor.scss';

function FileEditor() {
    const [messageApi, contextHolder] = message.useMessage();
    const [loading, setLoading] = useState(false);
    const [initLoading, setInitLoading] = useState(true);
    const [files, setFiles] = useState<FileModel[]>([]);
    const [currentFile, setCurrentFile] = useState<FileModel | undefined>(undefined);
    const [currentSelectedFile, setCurrentSelectedFile] = useState<SelectedFileSchema | undefined>(undefined);
    const [isEditFileModalOpen, setIsEditFileModalOpen] = useState(false);
    const [loadingEditFileModal, setLoadingEditFileModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        const bucketListResponse = await MainService.bucketList();

        if (bucketListResponse.success) {
            const bucketData = bucketListResponse.data as FileModel[];
            setFiles(sortEntries(bucketData));
            setInitLoading(false);
        } else {
            console.log("Error bucketListResponse", bucketListResponse);
            const error = bucketListResponse.data as ApiErrorData;
            messageApi.error(error.message as string || 'Hubo un error al realizar la acción, por favor inténtalo nuevamente.', 3.5);
            setInitLoading(false);
        }
    }

    const sortEntries = (entries: FileModel[]) => {
        return [...entries].sort((a, b) => {
            const aIsFolder = a.type === FileModelType.FOLDER ? 0 : 1;
            const bIsFolder = b.type === FileModelType.FOLDER ? 0 : 1;

            if (aIsFolder !== bIsFolder) {
                return aIsFolder - bIsFolder;
            }

            return a.key.localeCompare(b.key, undefined, { sensitivity: 'base' });
        });
    }

    const navigateToFolder = async (path: string, breadCrumb: ItemInterface[] = []) => {
        if (loading) {
            return;
        }

        setLoading(true);
        const bucketListResponse = await MainService.bucketList(path);

        if (bucketListResponse.success) {
            const bucketData = bucketListResponse.data as FileModel[];
            setFiles(sortEntries(bucketData));

            setSearchQuery('');

            if (!path) {
                setCurrentFile(undefined);
            } else {
                const lastCrumb = breadCrumb[breadCrumb.length - 1];
                setCurrentFile({
                    key: lastCrumb?.label || path,
                    type: FileModelType.FOLDER,
                    breadCrumb,
                    fullPath: path,
                });
            }
        } else {
            console.log("Error bucketListResponse", bucketListResponse);
            const error = bucketListResponse.data as ApiErrorData;
            messageApi.error(error.message as string || 'Hubo un error al realizar la acción, por favor inténtalo nuevamente.', 3.5);
        }

        setLoading(false);
    }

    const selectFile = async (file: FileModel) => {
        if (loading) {
            return;
        }

        if (file.type === FileModelType.FOLDER) {
            await navigateToFolder(file.fullPath, file.breadCrumb);
        } else if (file.type === FileModelType.BACK_FOLDER) {
            if (!currentFile) {
                return;
            }

            if (currentFile.breadCrumb.length > 1) {
                const previousBreadcrumb = currentFile.breadCrumb.slice(0, -1);
                const previousPath = String(previousBreadcrumb[previousBreadcrumb.length - 1].value);
                await navigateToFolder(previousPath, previousBreadcrumb);
            } else {
                await navigateToFolder('');
            }
        } else {
            setLoading(true);
            await showEditFileModal(file);
        }
    }

    const navigateBreadcrumb = async (index: number) => {
        if (!currentFile || loading) {
            return;
        }

        const nextBreadcrumb = currentFile.breadCrumb.slice(0, index + 1);
        const nextPath = String(nextBreadcrumb[nextBreadcrumb.length - 1].value);

        if (nextPath === currentFile.fullPath) {
            return;
        }

        await navigateToFolder(nextPath, nextBreadcrumb);
    }

    const showEditFileModal = async (file: FileModel) => {
        setLoadingEditFileModal(true);

        const fileContentResponse = await MainService.getFileContent(file.fullPath);

        if (fileContentResponse.success) {
            setCurrentSelectedFile({
                filename: file.key,
                extension: file.extension || '',
                content: fileContentResponse.data as string,
                path: file.fullPath
            });
            setIsEditFileModalOpen(true);
        } else {
            const error = fileContentResponse.data as ApiErrorData;
            messageApi.error(error.message as string || 'Hubo un error al realizar la acción, por favor inténtalo nuevamente.', 3.5);
        }

        setLoadingEditFileModal(false);
        setLoading(false);
    }

    const handleEditFileModalClose = () => {
        if (!loadingEditFileModal) {
            setIsEditFileModalOpen(false);
            setCurrentSelectedFile(undefined);
        }
    }

    const handleEditFileModalOk = async (key: string, newContent: string) => {
        if (!loadingEditFileModal) {
            setLoadingEditFileModal(true);
            const saveFileContentResponse = await MainService.saveFileContent(key, newContent);

            if (saveFileContentResponse.success) {
                messageApi.success('Se guardaron los cambios satisfactoriamente.', 3.5);
                setLoadingEditFileModal(false);
                setIsEditFileModalOpen(false);
                setCurrentSelectedFile(undefined);
            } else {
                const error = saveFileContentResponse.data as ApiErrorData;
                messageApi.error(error.message as string || 'Hubo un error al realizar la acción, por favor inténtalo nuevamente.', 3.5);
                setLoadingEditFileModal(false);
            }
        }
    }

    const breadcrumbItems = currentFile
        ? [
            {
                title: (
                    <span
                        className="file-editor-breadcrumb-link"
                        onClick={() => navigateToFolder('')}
                    >
                        ~
                    </span>
                ),
            },
            ...currentFile.breadCrumb.map((record, index) => {
                const isCurrent = index === currentFile.breadCrumb.length - 1;

                return {
                    title: isCurrent ? (
                        <span className="file-editor-breadcrumb-current">{record.label}</span>
                    ) : (
                        <span
                            className="file-editor-breadcrumb-link"
                            onClick={() => navigateBreadcrumb(index)}
                        >
                            {record.label}
                        </span>
                    ),
                };
            }),
        ]
        : [{ title: <span className="file-editor-breadcrumb-current">~</span> }];

    const filteredFiles = searchQuery.trim()
        ? files.filter((item) =>
            item.key.toLowerCase().includes(searchQuery.trim().toLowerCase())
        )
        : files;

    const listItems: FileModel[] = currentFile
        ? [
            {
                key: '..',
                type: FileModelType.BACK_FOLDER,
                breadCrumb: [],
                fullPath: '/',
            },
            ...filteredFiles,
        ]
        : filteredFiles;

    const getDisplayName = (item: FileModel) => {
        if (item.type === FileModelType.FOLDER) {
            return item.key.replace(/\/$/, '');
        }
        return item.key;
    }

    const renderIcon = (item: FileModel) => {
        if (item.type === FileModelType.BACK_FOLDER) {
            return (
                <span className="file-editor-item-icon-wrap file-editor-item-icon-wrap--back">
                    <ArrowUpOutlined className="file-editor-item-icon" />
                </span>
            );
        }

        if (item.type === FileModelType.FOLDER) {
            return (
                <span className="file-editor-item-icon-wrap file-editor-item-icon-wrap--folder">
                    <FolderFilled className="file-editor-item-icon" />
                </span>
            );
        }

        return (
            <span className="file-editor-item-icon-wrap file-editor-item-icon-wrap--file">
                <FileOutlined className="file-editor-item-icon" />
            </span>
        );
    }

    return (
        <>
            {contextHolder}

            <Layout breadcrumb={[
                { title: <span><FileTextOutlined /> Update Secrets</span> }
            ]}>
                <div className="file-editor">
                    <div className="file-editor-panel">
                        <div className="file-editor-toolbar">
                            <div className="file-editor-path">
                                <Breadcrumb
                                    className="file-editor-breadcrumb"
                                    separator={<span className="file-editor-breadcrumb-sep">/</span>}
                                    items={breadcrumbItems}
                                />
                            </div>
                            <div className="file-editor-toolbar-right">
                                <Input
                                    allowClear
                                    size="middle"
                                    className="file-editor-search"
                                    placeholder="Buscar…"
                                    prefix={<SearchOutlined />}
                                    value={searchQuery}
                                    onChange={(event) => setSearchQuery(event.target.value)}
                                />
                                <span className="file-editor-count">
                                    {filteredFiles.length}
                                    {searchQuery.trim() ? ` / ${files.length}` : ''}
                                </span>
                            </div>
                        </div>

                        <Spin spinning={initLoading || loading}>
                            <div className="file-editor-list" role="list">
                                {!initLoading && listItems.length === 0 ? (
                                    <Empty
                                        className="file-editor-empty"
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description={searchQuery.trim() ? 'Sin resultados' : 'Carpeta vacía'}
                                    />
                                ) : (
                                    listItems.map((item) => (
                                        <button
                                            key={`${item.type}-${item.fullPath}-${item.key}`}
                                            type="button"
                                            className={`file-editor-item ${
                                                item.type === FileModelType.BACK_FOLDER ? 'file-editor-item--back' : ''
                                            } ${
                                                item.type === FileModelType.FOLDER ? 'file-editor-item--folder' : ''
                                            } ${
                                                item.type === FileModelType.DOCUMENT ? 'file-editor-item--file' : ''
                                            }`}
                                            onClick={() => selectFile(item)}
                                            disabled={loading}
                                        >
                                            {renderIcon(item)}
                                            <span className="file-editor-item-body">
                                                <span className="file-editor-item-name">{getDisplayName(item)}</span>
                                                {item.type === FileModelType.DOCUMENT && item.extension && (
                                                    <span className="file-editor-item-badge">.{item.extension}</span>
                                                )}
                                            </span>
                                            {(item.type === FileModelType.FOLDER || item.type === FileModelType.BACK_FOLDER) && (
                                                <RightOutlined className="file-editor-item-chevron" />
                                            )}
                                        </button>
                                    ))
                                )}
                            </div>
                        </Spin>
                    </div>
                </div>

                {currentSelectedFile && (
                    <EditFileModal
                        selectedFile={currentSelectedFile}
                        handleModalClose={handleEditFileModalClose}
                        isModalOpen={isEditFileModalOpen}
                        loading={loadingEditFileModal}
                        handleModalOK={handleEditFileModalOk}
                    />
                )}
            </Layout>
        </>
    );
}

export default FileEditor;
